import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useQueue } from '@/hooks/useQueue'
import type { QueueOrder } from '@/lib/schemas/queue'
import { filterByCategory, searchOrders, type QueueFilter } from '@/lib/queue-filters'

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(d)
}

const columns: ColumnDef<QueueOrder>[] = [
  {
    id: 'order',
    header: 'Order',
    cell: ({ row }) => (
      <Link
        to={`/queue/${row.original.shopify_order_id}`}
        className="font-medium text-blue-700 tabular-nums hover:underline"
      >
        #{row.original.order_number}
      </Link>
    ),
  },
  {
    id: 'customer',
    header: 'Customer',
    cell: ({ row }) => (
      <span>
        {row.original.customer_name}
        {row.original.is_vip && (
          <span title="VIP" aria-label="VIP" className="ml-1">
            ⭐
          </span>
        )}
      </span>
    ),
  },
  {
    id: 'kh_items',
    header: 'KH Items',
    cell: ({ row }) => (
      <span className="tabular-nums">{row.original.kh_item_count} items</span>
    ),
  },
  {
    id: 'kh_value',
    header: 'KH Value',
    cell: ({ row }) => <span className="tabular-nums">${row.original.kh_value}</span>,
  },
  {
    id: 'order_date',
    header: 'Order Date',
    cell: ({ row }) => (
      <span className="tabular-nums">{formatDate(row.original.order_date)}</span>
    ),
  },
  {
    id: 'eligible',
    header: 'Eligible Items',
    cell: ({ row }) => (
      <span className="tabular-nums">
        {row.original.eligible_count} of {row.original.kh_item_count}
      </span>
    ),
  },
  {
    id: 'action',
    header: 'Action',
    cell: ({ row }) => (
      <Link
        to={`/queue/${row.original.shopify_order_id}`}
        className="border border-gray-800 px-2 py-1 text-xs hover:bg-gray-900 hover:text-white"
      >
        Review →
      </Link>
    ),
  },
]

const FILTERS: { key: QueueFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'eligible', label: 'Has Eligible Items' },
  { key: 'vip', label: 'VIP Only' },
]

export default function Queue() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQueue()
  const [filter, setFilter] = useState<QueueFilter>('all')
  const [search, setSearch] = useState('')

  const orders = useMemo(() => data?.orders ?? [], [data])
  const visibleOrders = useMemo(
    () => searchOrders(filterByCategory(orders, filter), search),
    [orders, filter, search],
  )

  const table = useReactTable({
    data: visibleOrders,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">Dropship Queue</h1>
        {isFetching && !isLoading && (
          <span className="text-xs text-gray-400">Refreshing…</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`border px-3 py-1 text-sm ${
                filter === f.key
                  ? 'border-gray-800 bg-gray-900 text-white'
                  : 'border-gray-300 text-gray-600 hover:text-black'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search order #, customer, or SKU"
          aria-label="Search queue"
          className="w-64 border border-gray-300 px-2 py-1 text-sm"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading queue…</p>
      ) : isError ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Failed to load the queue.'}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white"
          >
            Retry
          </button>
        </div>
      ) : orders.length === 0 ? (
        <p className="text-sm text-gray-500">No orders in the queue</p>
      ) : visibleOrders.length === 0 ? (
        <p className="text-sm text-gray-500">No orders match your filters</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b border-gray-200">
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase whitespace-nowrap"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-gray-100 last:border-b-0">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2 whitespace-nowrap">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
